/**
 * "Add your own …": the company's own module, inverter, microinverter, optimizer or battery, with the electrical
 * values a plan set needs (lib/permitEquipment.ts SPEC_FIELDS, the same rule as the server) and an optional datasheet
 * PDF (stored like the company's other files; the plan set appends it). Only the company sees it.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileUp, Plus } from "lucide-react";
import { useState } from "react";
import { api, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { Alert, Button, Field, Modal, Segmented, TextInput, useToast } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import { KIND_LABEL, SPEC_FIELDS, kindsForRole, specsFromForm, type EquipmentKind, type EquipmentRole, type SpecValues } from "../lib/permitEquipment";

export function CustomEquipment({ orgId, role, initial, onCreated, onClose }: {
  orgId: string;
  role: EquipmentRole;
  initial?: string;
  onCreated: (item: S["PermitEquipment"]) => void;
  onClose: () => void;
}) {
  const t = useT();
  const kinds = kindsForRole(role);
  const toast = useToast();
  const qc = useQueryClient();
  const [kind, setKind] = useState<EquipmentKind>(kinds[0]!);
  const words = (initial ?? "").trim().split(/\s+/).filter(Boolean);
  const [manufacturer, setManufacturer] = useState(words.length > 1 ? words[0]! : "");
  const [model, setModel] = useState(words.length > 1 ? words.slice(1).join(" ") : words[0] ?? "");
  const [values, setValues] = useState<SpecValues>({});
  const [datasheetUrl, setDatasheetUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      const { specs, error: problem } = specsFromForm(t, kind, values);
      if (!manufacturer.trim() || !model.trim()) throw new Error(t("permit.custom.error.names"));
      if (problem) throw new Error(problem);
      if (file && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) throw new Error(t("permit.custom.error.pdf"));
      let item = await unwrap(
        api.POST("/v1/orgs/{org_id}/permit-equipment", {
          params: { path: { org_id: orgId } },
          body: { kind, manufacturer: manufacturer.trim(), model: model.trim(), specs, datasheet_url: datasheetUrl.trim() || null, verified: false, active: true },
        }),
      );
      if (file) {
        const form = new FormData();
        form.append("file", file);
        const r = await rawFetch(`/api/v1/orgs/${orgId}/permit-equipment/${item.id}/datasheet`, { method: "POST", body: form });
        item = (await r.json()) as S["PermitEquipment"];
      }
      return item;
    },
    onSuccess: (item) => {
      qc.invalidateQueries({ queryKey: ["permit-equipment-search"] });
      qc.setQueryData(["permit-equipment-item", item.id], item);
      toast(t("permit.custom.added", { name: `${item.manufacturer} ${item.model}` }));
      onCreated(item);
    },
    onError: (e) => setError(errorMessage(e)),
  });
  return (
    <Modal
      title={t("permit.custom.title", { role })}
      onClose={onClose}
      width={720}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" icon={<Plus size={16} />} loading={save.isPending} onClick={() => save.mutate()}>
            {t("permit.custom.submit")}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <p className="small muted" style={{ margin: 0 }}>
          {t("permit.custom.intro")}
        </p>
        {kinds.length > 1 && <Segmented value={kind} onChange={(k) => setKind(k as EquipmentKind)} options={kinds.map((k) => ({ value: k, label: t(KIND_LABEL[k]) }))} />}
        <div className="pw-spec-grid">
          <Field label={t("permit.custom.manufacturer")}>
            <TextInput value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} maxLength={120} />
          </Field>
          <Field label={t("permit.custom.model")}>
            <TextInput value={model} onChange={(e) => setModel(e.target.value)} maxLength={160} />
          </Field>
        </div>
        <div className="pw-spec-grid">
          {SPEC_FIELDS[kind].map((f) => (
            <Field key={f.key} label={<span className={f.required ? "req" : undefined}>{f.unit ? t("permit.custom.fieldWithUnit", { label: t(f.label), unit: f.unit }) : t(f.label)}</span>}>
              {f.kind === "select" ? (
                <select className="input" aria-label={t(f.label)} value={values[f.key] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}>
                  <option value="">—</option>
                  {f.options!.map((o) => (
                    <option key={o.value} value={o.value}>
                      {t(o.label)}
                    </option>
                  ))}
                </select>
              ) : (
                <TextInput aria-label={t(f.label)} inputMode="decimal" value={values[f.key] ?? ""} placeholder={f.hint ? t(f.hint) : undefined} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />
              )}
            </Field>
          ))}
        </div>
        <div className="pw-spec-grid">
          <Field label={t("permit.custom.datasheetLink")}>
            {/* eslint-disable-next-line solaros-i18n/no-hardcoded-text -- a URL pattern, the same in every language */}
            <TextInput value={datasheetUrl} onChange={(e) => setDatasheetUrl(e.target.value)} placeholder="https://…" maxLength={1000} />
          </Field>
          <Field label={t("permit.custom.datasheetPdf")} hint={t("permit.custom.datasheetPdfHint")}>
            <label className="btn btn-sm" style={{ justifyContent: "flex-start" }}>
              <FileUp size={14} /> {file ? <span dir="auto">{file.name}</span> : t("permit.custom.choosePdf")}
              <input type="file" accept="application/pdf,.pdf" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
          </Field>
        </div>
        {error && <Alert tone="danger" title={t("permit.custom.notAdded")}>{error}</Alert>}
      </div>
    </Modal>
  );
}
